import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import { MyPlanService } from "./MyPlanSlotForm";

export interface Colleague {
    key: string;
    displayName: string;
}

interface CandidateSlot {
    serviceId: string;
    slot: 1 | 2;
    date: string;
    serviceLabel: string;
}

interface MyPlanActionsProps {
    token: string;
    service: MyPlanService;
    colleagues: Colleague[];
    formatDate: (isoDate: string) => string;
    /** Called after something changed on the server; the page reloads the plan. */
    onDone: (message: string) => Promise<void>;
}

type Panel = "none" | "swap" | "decline";

/**
 * What a preacher can do about a slot besides filling it in: ask a colleague to
 * swap, withdraw that request, or say that they cannot come.
 */
export default function MyPlanActions({ token, service, colleagues, formatDate, onDone }: MyPlanActionsProps) {
    const { t } = useTranslation();
    const [panel, setPanel] = useState<Panel>("none");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const [reason, setReason] = useState("");
    const [toKey, setToKey] = useState("");
    const [candidates, setCandidates] = useState<CandidateSlot[] | null>(null);
    const [counter, setCounter] = useState("");
    const [message, setMessage] = useState("");

    const slotRef = { token, serviceId: service.serviceId, slot: service.slot };
    const idSuffix = `-${service.serviceId}-${service.slot}`;

    const run = async (name: string, data: object, doneMessage: string) => {
        setBusy(true);
        setError("");
        try {
            await httpsCallable(functions, name)({ ...slotRef, ...data });
            await onDone(doneMessage);
        } catch (err: unknown) {
            setError((err as { message?: string }).message || (t("errors.anErrorOccurred") as string));
        } finally {
            setBusy(false);
        }
    };

    const chooseColleague = async (key: string) => {
        setToKey(key);
        setCounter("");
        setCandidates(null);
        if (!key) return;
        try {
            const load = httpsCallable<{ token: string; toKey: string }, { slots: CandidateSlot[] }>(functions, "getSwapCandidates");
            setCandidates((await load({ token, toKey: key })).data.slots);
        } catch {
            // The counter offer is optional; without the list a plain request is still possible.
            setCandidates([]);
        }
    };

    const submitSwap = (e: React.FormEvent) => {
        e.preventDefault();
        const [counterServiceId, counterSlot] = counter ? counter.split("|") : [];
        run(
            "requestSwap",
            { toKey, message: message.trim(), ...(counter ? { counterServiceId, counterSlot: Number(counterSlot) } : {}) },
            t("myPlan.swapRequested") as string
        );
    };

    const submitDecline = (e: React.FormEvent) => {
        e.preventDefault();
        run("declineMySlot", { reason: reason.trim() }, t("myPlan.declined") as string);
    };

    if (service.status === "swap-pending") {
        return (
            <div className="mt-3 pt-3 border-top">
                <p className="mb-2">
                    {service.swapWith ? t("myPlan.swapPendingWith", { name: service.swapWith }) : t("myPlan.swapPending")}
                </p>
                {error && <Alert variant="danger">{error}</Alert>}
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => run("cancelMySwap", {}, t("myPlan.swapCancelled") as string)}
                >
                    {t("myPlan.swapCancel")}
                </Button>
            </div>
        );
    }

    return (
        <div className="mt-3 pt-3 border-top">
            <div className="d-flex gap-2 flex-wrap">
                <Button
                    variant={panel === "swap" ? "secondary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setPanel(panel === "swap" ? "none" : "swap")}
                >
                    {t("myPlan.swapOpen")}
                </Button>
                <Button
                    variant={panel === "decline" ? "secondary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setPanel(panel === "decline" ? "none" : "decline")}
                >
                    {t("myPlan.declineOpen")}
                </Button>
            </div>

            {panel === "swap" && (
                <Form onSubmit={submitSwap} className="mt-3">
                    <Form.Group className="mb-3" controlId={`swapTo${idSuffix}`}>
                        <Form.Label>{t("myPlan.swapColleague")}</Form.Label>
                        <Form.Select value={toKey} onChange={(e) => chooseColleague(e.target.value)} required>
                            <option value="">{t("myPlan.swapColleaguePlaceholder")}</option>
                            {colleagues.map((colleague) => (
                                <option key={colleague.key} value={colleague.key}>
                                    {colleague.displayName}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    {toKey && (
                        <Form.Group className="mb-3" controlId={`swapCounter${idSuffix}`}>
                            <Form.Label>{t("myPlan.swapCounter")}</Form.Label>
                            <Form.Select value={counter} onChange={(e) => setCounter(e.target.value)} disabled={candidates === null}>
                                <option value="">{t("myPlan.swapNoCounter")}</option>
                                {(candidates ?? []).map((slot) => (
                                    <option key={`${slot.serviceId}|${slot.slot}`} value={`${slot.serviceId}|${slot.slot}`}>
                                        {formatDate(slot.date)} · {slot.serviceLabel}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">{t("myPlan.swapCounterHint")}</Form.Text>
                        </Form.Group>
                    )}
                    <Form.Group className="mb-3" controlId={`swapMessage${idSuffix}`}>
                        <Form.Label>{t("myPlan.swapMessage")}</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} />
                    </Form.Group>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Button type="submit" disabled={busy || !toKey} className="w-100">
                        {busy && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                        {t("myPlan.swapSubmit")}
                    </Button>
                    <Form.Text className="text-muted d-block mt-2">{t("myPlan.swapHint")}</Form.Text>
                </Form>
            )}

            {panel === "decline" && (
                <Form onSubmit={submitDecline} className="mt-3">
                    <Form.Group className="mb-3" controlId={`declineReason${idSuffix}`}>
                        <Form.Label>{t("myPlan.declineReason")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            maxLength={500}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            required
                        />
                    </Form.Group>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Button type="submit" variant="danger" disabled={busy || !reason.trim()} className="w-100">
                        {busy && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                        {t("myPlan.declineSubmit")}
                    </Button>
                    <Form.Text className="text-muted d-block mt-2">{t("myPlan.declineHint")}</Form.Text>
                </Form>
            )}
        </div>
    );
}
